import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Grid,
  Chip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { CloudUpload, Delete, Download, Visibility, Close, InsertDriveFile } from '@mui/icons-material';

function FileManager() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const { default: api } = await import('../services/api');
      const response = await api.get('/file-manager');
      setFiles(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch files:', error);
      showAlert('Failed to fetch files', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    validateAndSetFile(file);
  };

  const validateAndSetFile = (file) => {
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (file && allowedTypes.includes(file.type)) {
      setSelectedFile(file);
      setFileName(file.name);
    } else {
      showAlert('Please select a valid file (Excel, PDF, JPEG, PNG, Word)', 'error');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    validateAndSetFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showAlert('Please select a file', 'error');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    if (fileName) {
      formData.append('fileName', fileName);
    }

    try {
      const { default: api } = await import('../services/api');
      await api.post('/file-manager/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      showAlert('File uploaded successfully', 'success');
      setUploadModalOpen(false);
      setSelectedFile(null);
      setFileName('');
      fetchFiles();
    } catch (error) {
      console.error('Upload error:', error);
      showAlert(error.response?.data?.message || 'Failed to upload file', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      const { default: api } = await import('../services/api');
      await api.delete(`/file-manager/${id}`);
      showAlert('File deleted successfully', 'success');
      fetchFiles();
    } catch (error) {
      showAlert('Failed to delete file', 'error');
    }
  };

  const handleDownload = async (id, originalName) => {
    try {
      const { default: api } = await import('../services/api');
      const response = await api.get(`/file-manager/download/${id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showAlert('Failed to download file', 'error');
    }
  };

  const showAlert = (message, severity) => {
    setAlert({ show: true, message, severity });
    setTimeout(() => setAlert({ show: false, message: '', severity: 'success' }), 3000);
  };

  const getFileType = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    const typeMap = {
      xlsx: 'Excel',
      xls: 'Excel',
      pdf: 'PDF',
      jpg: 'Image',
      jpeg: 'Image',
      png: 'Image',
      doc: 'Word',
      docx: 'Word'
    };
    return typeMap[ext] || 'File';
  };

  const getFileTypeColor = (filename) => {
    const type = getFileType(filename);
    const colorMap = {
      Excel: 'success',
      PDF: 'error',
      Image: 'info',
      Word: 'primary',
      File: 'default'
    };
    return colorMap[type];
  };

  const columns = [
    { field: 'sno', headerName: 'S.No', width: 80 },
    { field: 'fileName', headerName: 'File Name', flex: 1, minWidth: 250 },
    {
      field: 'fileType',
      headerName: 'Type',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={getFileType(params.row.originalName)}
          color={getFileTypeColor(params.row.originalName)}
          size="small"
        />
      )
    },
    {
      field: 'uploadedAt',
      headerName: 'Uploaded Date',
      width: 180,
      renderCell: (params) => new Date(params.value).toLocaleString()
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleDownload(params.row.id, params.row.originalName)}
          >
            <Download />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(params.row.id)}
          >
            <Delete />
          </IconButton>
        </Box>
      )
    }
  ];

  const rows = files.map((file, index) => ({
    id: file._id,
    sno: index + 1,
    fileName: file.fileName,
    originalName: file.originalName,
    uploadedAt: file.uploadedAt
  }));

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          File Manager
        </Typography>
        <Button
          variant="contained"
          startIcon={<CloudUpload />}
          onClick={() => setUploadModalOpen(true)}
        >
          Upload File
        </Button>
      </Box>

      {alert.show && (
        <Alert severity={alert.severity} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      <Card>
        <CardContent>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            pageSize={10}
            autoHeight
            disableRowSelectionOnClick
            sx={{
              boxShadow: 2,
              backgroundColor: 'white',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f8f9fa'
              }
            }}
          />
        </CardContent>
      </Card>

      <Dialog open={uploadModalOpen} onClose={() => setUploadModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Upload File
            <IconButton onClick={() => setUploadModalOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              border: `2px dashed ${dragOver ? '#1976d2' : '#ccc'}`,
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              bgcolor: dragOver ? '#f0f7ff' : '#fafafa',
              cursor: 'pointer',
              mb: 2
            }}
            onClick={() => document.getElementById('file-upload-input').click()}
          >
            <CloudUpload sx={{ fontSize: 48, color: '#1976d2', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              {selectedFile ? selectedFile.name : 'Drag & Drop File Here'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Supported: Excel, PDF, JPEG, PNG, Word
            </Typography>
            <input
              id="file-upload-input"
              type="file"
              accept=".xlsx,.xls,.pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </Box>
          <TextField
            fullWidth
            label="File Name (Optional)"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            placeholder="Enter custom file name"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadModalOpen(false)}>Cancel</Button>
          <Button onClick={handleUpload} variant="contained" disabled={!selectedFile}>
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FileManager;
